import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useModalAnimation } from '../../hooks/useModalAnimation';
import { useModalCloseProtection } from '../../hooks/useModalCloseProtection';
import { lockScroll, unlockScroll } from '../../utils/scrollLock';

interface FullScreenShellProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly title?: string;
  readonly children: React.ReactNode;
  readonly icon?: React.ReactNode;
  readonly showCloseButton?: boolean;
  readonly onBeforeClose?: () => Promise<boolean> | boolean;
  readonly header?: React.ReactNode;
  readonly variant?: 'fullpage' | 'centered';
}

export const FullScreenShell: React.FC<FullScreenShellProps> = ({
  isOpen,
  onClose,
  title,
  children,
  icon,
  showCloseButton = true,
  onBeforeClose,
  header,
  variant = 'centered'
}) => {
  const { isMounted, dataState } = useModalAnimation(isOpen);
  const { modalRef, handleClose } = useModalCloseProtection({
    isOpen,
    onClose,
    onBeforeClose
  });

  // Bloqueo de scroll mientras está montado (apertura + cierre animado).
  useEffect(() => {
    if (!isMounted) return;
    lockScroll();
    return () => unlockScroll();
  }, [isMounted]);

  let defaultHeader: React.ReactNode = null;
  if (header) {
    defaultHeader = header;
  } else if (title) {
    defaultHeader = (
      <div className="bg-gradient-to-r from-slate-700 to-slate-800 p-6 flex items-center justify-between rounded-t-2xl">
        <div className="flex items-center gap-3">
          {icon}
          <h2 className="text-2xl font-bold text-white">{title}</h2>
        </div>
        {showCloseButton && (
          <button
            onClick={handleClose}
            className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        )}
      </div>
    );
  }

  const backdropClassName = variant === 'fullpage'
    ? 'fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4'
    : 'fixed inset-0 z-50 bg-black/50 flex items-center justify-center overflow-y-auto p-4';

  const contentClassName = variant === 'fullpage'
    ? 'w-full max-w-6xl my-4 bg-gray-50 rounded-2xl shadow-2xl flex flex-col min-h-screen md:min-h-0'
    : 'w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col';

  return isMounted && createPortal(
    <div
      data-modal-overlay="true"
      className={backdropClassName}
    >
      <div
        ref={modalRef}
        data-state={dataState}
        className={`relative ${contentClassName} animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:duration-200 data-[state=closed]:duration-150`}
      >
        {defaultHeader}
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};
